import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineCalculate,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdOutlinePlayCircle,
    MdSuperscript
} from 'react-icons/md';
import moment from 'moment';
import Player from '@vimeo/player';
import Button from 'components/Button';
import TextInput from 'components/TextInput';
import Loader from 'components/Loader';
import Popup from 'components/Popup';
import axios from 'config/axios';
import { useAppDispatch } from 'hooks/useStore';
import { setError } from 'store/error';
import { MULTIPLE_CHOICES } from 'global/constants';
import { getDeviceOS } from 'global/os';
import { generatePassageHTML } from 'global/passageHighlight';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Reference from 'pages/private/Digital/Module/Reference';
import Shortcuts from 'pages/private/Digital/Module/Shortcuts';
import FigureViewer from 'pages/private/Digital/FigureViewer';
import ToolErrorBoundary from 'pages/private/Digital/ToolErrorBoundary';
import DesmosCalculatorPanel from 'components/DesmosCalculatorPanel';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

const UNDERLINE = {
    'Solid': 'solid',
    'Dashed': 'dashed',
    'Dotted': 'dotted'
};

function DigitalReview() {
    const [studentName, setStudentName] = useState('');
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [dividerRatio, setDividerRatio] = useState(0.5);
    const testRef = useRef<HTMLDivElement>(null);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const [annotations, setAnnotations] = useState([]);
    const [player, setPlayer] = useState(null);
    const [explanationVideo, setExplanationVideo] = useState(null);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));
    const explanationRef = useOutsideClickHandler(() => setExplanationVideo(null));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate(-1);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('digitalReview__passages');
        let question = document.getElementById('digitalReview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const generateAnnotation = () => {
        const newAnnotations = [ ...testModules[moduleIndex].answers[questionIndex].annotations ];

        for (let i = 0; i < newAnnotations.length; i++) {
            const {
                passageId,
                startIndex,
                endIndex
            } = newAnnotations[i];

            const passage = document.getElementById(passageId);

            // Skip annotations whose passage element isn't on the page rather
            // than crash.
            if (!passage) continue;

            const { passageHTML, passagePreview } = generatePassageHTML(passage, startIndex, endIndex);

            newAnnotations[i].html = passageHTML;
            newAnnotations[i].preview = passagePreview;
        }

        setAnnotations(newAnnotations);
    }

    const handleAnnotationMouseOver = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.left = event.clientX + 'px';
            bubble.style.top = event.clientY + 'px';
            bubble.style.display = 'block';
        }

        return false;
    }

    const handleAnnotationMouseOut = (
        _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.display = 'none';
        }

        return false;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            // console.log(wrappingTexts[i]);
            if (testModules[moduleIndex].subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'KeyT':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyV':
                //     markQuestion();
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (calculatorOpen) setCalculatorOpen(false);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                case 'KeyR':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit5':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit6':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                // case 'Digit7':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit8':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit9':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit0':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                // case 'KeyG':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (calculatorOpen) setCalculatorOpen(false);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                case 'KeyH':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (testModules[moduleIndex].subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                // case 'KeyV':
                //     markQuestion();
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    // const dragMove = (element: HTMLElement, header: HTMLElement) => {
    //     let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

    //     const dragMouseDown = (event) => {
    //         event = event || window.event;
    //         event.preventDefault();
    
    //         pos3 = event.clientX;
    //         pos4 = event.clientY;
    //         document.onmouseup = closeDragElement;
    //         document.onmousemove = dragElement;
    //     }

    //     const dragElement = (event) => {
    //         event = event || window.event;
    //         event.preventDefault();

    //         pos1 = pos3 - event.clientX;
    //         pos2 = pos4 - event.clientY;
    //         pos3 = event.clientX;
    //         pos4 = event.clientY;

    //         element.style.top = (element.offsetTop - pos2) + 'px';
    //         element.style.left = (element.offsetLeft - pos1) + 'px';
    //     }

    //     const closeDragElement = () => {
    //         document.onmouseup = null;
    //         document.onmousemove = null;
    //     }
    
    //     header.onmousedown = dragMouseDown;
    //     header.ontouchstart = dragMouseDown;
    // }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    
        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }
    
            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    };

    useEffect(() => {
        axios.get(`tests/digital/sat/review/${testId}/`)
            .then(res => {
                // console.log(res.data);
                setStudentName(res.data.studentName);
                setTestModules(res.data.testModules);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    useEffect(() => {
        if (testModules) {
            setShortcutKeyPressed(true);

            // dragMove(document.getElementById('calculatorDrag'), document.getElementById('calculatorHeader'));
            // dragMove(document.getElementById('referenceDrag'), document.getElementById('referenceHeader'));
            // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
            const referenceDragEl = document.getElementById('referenceDrag');
            const referenceHeaderEl = document.getElementById('referenceHeader');
            if (referenceDragEl && referenceHeaderEl) {
                dragMove(referenceDragEl, referenceHeaderEl);
            }
        
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }
            }
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
            generateAnnotation();
        }
    }, [testModules, moduleIndex, questionIndex]);

    useEffect(() => {
        if (explanationVideo) {
            setPlayer(new Player('vimeoPlayer', {
                url: explanationVideo
            }));
        }
        else {
            setPlayer(null);
        }
    }, [explanationVideo]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen, annotations]);

    const handleDividerPointerDown = (event: React.PointerEvent) => {
        event.preventDefault();
        const onMove = (e: PointerEvent) => {
            const rect = testRef.current?.getBoundingClientRect();
            if (!rect || rect.width === 0) return;
            const ratio = (e.clientX - rect.left) / rect.width;
            setDividerRatio(Math.max(0.2, Math.min(0.8, ratio)));
        };
        const onUp = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onUp);
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
    };

    useEffect(() => {
        if (getDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (getDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (getDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (getDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        answers,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <div className='digitalReviewBox' onCopy={(event) => { event.preventDefault() }}>
            {explanationVideo &&
                <Popup
                    secondaryButtonText='CLOSE'
                    secondaryButtonOnClick={() => setExplanationVideo(null)}
                    innerRef={explanationRef}
                >
                    <div className='digitalReview__videoContainer'>
                        <div id='vimeoPlayer'></div>
                    </div>
                </Popup>
            }
            <div className='digitalReview'>
                <div className='digitalReview__header'>
                    <div className='digitalReview__titleGroup'>
                        <button className='digitalReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='digitalReview__title'>
                                {name}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='digitalReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='digitalReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='digitalReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'digitalReview__moduleSelectDropdownItem--selected'
                                                : 'digitalReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.name}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='digitalReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='digitalReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='digitalReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    <div className='digitalReview__timerGroup'>
                        <p className='digitalReview__timer'>
                            {formatTime(answers[questionIndex].timeSpent)}
                        </p>
                        <p className='digitalReview__timerText'>
                            Time spent
                        </p>
                    </div>
                    <div className='digitalReview__actionGroup'>
                        {subject === 'Math' && <>
                            <button
                                className={referenceOpen
                                    ? 'digitalReview__action--active'
                                    : 'digitalReview__action'
                                }
                                onClick={() => {
                                    if (calculatorOpen) setCalculatorOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setReferenceOpen(!referenceOpen);
                                }}
                            >
                                <MdSuperscript className='digitalReview__actionIcon' />
                                <p className='digitalReview__actionText'>Reference</p>
                            </button>
                            <button 
                                className={calculatorOpen
                                    ? 'digitalReview__action--active'
                                    : 'digitalReview__action'
                                }
                                onClick={() => {
                                    if (referenceOpen) setReferenceOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='digitalReview__actionIcon' />
                                <p className='digitalReview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className={shortcutsOpen
                                ? 'digitalReview__action--active'
                                : 'digitalReview__action'
                            }
                            onClick={() => {
                                if (calculatorOpen) setCalculatorOpen(false);
                                if (referenceOpen) setReferenceOpen(false);
                                setShortcutsOpen(!shortcutsOpen);
                            }}
                        >
                            <MdOutlineKeyboard className='digitalReview__actionIcon' />
                            <p className='digitalReview__actionText'>Shortcuts</p>
                        </button>
                        <button
                            className='digitalReview__action'
                            onClick={() => navigate(-1)}
                            // onClick={() => navigate(`/report/digital-sat/${testId}`)}
                        >
                            <MdExitToApp className='digitalReview__actionIcon' />
                            <p className='digitalReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='digitalReview__directions' ref={directionsRef}>
                            <div className='digitalReview__directionsBubble'>
                                <p className='katex digitalReview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='digitalReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }
                    <DesmosCalculatorPanel
                        open={calculatorOpen}
                        onClose={() => setCalculatorOpen(false)}
                        headerClassName='digitalReview__popupHeader'
                    />
                    <div id='referenceDrag' className={referenceOpen
                        ? 'digitalReview__popup--reference'
                        : 'digitalReview__popup--hidden'
                    }>
                        <div id='referenceHeader' className='digitalReview__popupHeader'>
                            <h3 className='digitalReview__popupHeaderText'>Reference</h3>
                            <MdClose className='digitalReview__popupHeaderIcon' onClick={() => setReferenceOpen(false)} />
                        </div>
                        <Reference />
                    </div>
                    <div id='shortcutsDrag' className={shortcutsOpen
                        ? 'digitalReview__popup--shortcuts'
                        : 'digitalReview__popup--hidden'
                    }>
                        <div id='shortcutsHeader' className='digitalReview__popupHeader'>
                            <h3 className='digitalReview__popupHeaderText'>
                                Keyboard shortcuts{getDeviceOS() === 'windows'
                                    ? ': Windows'
                                    : getDeviceOS() === 'mac_os'
                                    ? ': MacOS'
                                    : ''
                                }
                            </h3>
                            <MdClose className='digitalReview__popupHeaderIcon' onClick={() => setShortcutsOpen(false)} />
                        </div>
                        <Shortcuts />
                    </div>
                </div>
                <div className='digitalReview__dottedLine'></div>
                <div className='digitalReview__test' ref={testRef}>
                    {questions[questionIndex].passages.length > 0 && <>
                        <div className='digitalReview__material' style={{ flex: `${dividerRatio} 1 0%` }}>
                            <div className='digitalReview__materialContent'>
                                <div id='digitalReview__passages' className='digitalReview__passages'>
                                    {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='digitalReview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))}
                                    {questions[questionIndex].passages.map(({ id, passage }) => (
                                        <div key={id} className='digitalReview__passage'>
                                            <p
                                                id={id}
                                                className='katex digitalReview__passageText'
                                            >
                                                {passage}
                                            </p>
                                            {annotations.map((annotation, a) => <>
                                                <div
                                                    // className='katex digitalReview__passageAnnotation'
                                                    className={`katex digitalReview__passageAnnotation
                                                        ${annotation.color && 'digitalReview__passageAnnotation--' + COLORS[annotation.color]}
                                                        ${annotation.underline && 'digitalReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                    `}
                                                    onMouseOver={(event) => handleAnnotationMouseOver(event, `digitalReview__passageAnnotationBubble${a}`)}
                                                    onMouseOut={(event) => handleAnnotationMouseOut(event, `digitalReview__passageAnnotationBubble${a}`)}
                                                    dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                ></div>
                                                {annotation.annotation &&
                                                    <p
                                                        id={`digitalReview__passageAnnotationBubble${a}`}
                                                        className='digitalReview__passageAnnotationBubble'
                                                    >
                                                        {annotation.annotation}
                                                    </p>
                                                }
                                            </>)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div
                            className='digitalReview__divider'
                            onPointerDown={handleDividerPointerDown}
                            role='separator'
                            aria-orientation='vertical'
                        >
                            <div className='digitalReview__dividerGrip'></div>
                        </div>
                    </>}
                    <div className='digitalReview__question' style={questions[questionIndex].passages.length > 0 ? { flex: `${1 - dividerRatio} 1 0%` } : undefined}>
                        <div className='digitalReview__questionContent'>
                            <div className='digitalReview__questionHeader'>
                                <p className={answers[questionIndex].isCorrect
                                    ? 'digitalReview__questionNumber--correct'
                                    : 'digitalReview__questionNumber--wrong'
                                }>
                                    {questionIndex + 1}
                                </p>
                                <button className='digitalReview__questionMarkButton' onClick={() => {}}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='digitalReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='digitalReview__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                <button
                                    className={showAnswer
                                        ? 'digitalReview__showAnswer--active'
                                        : 'digitalReview__showAnswer'
                                    }
                                    onClick={() => setShowAnswer(!showAnswer)}
                                >
                                    {showAnswer ? 'Hide answer' : 'Show answer'}
                                </button>
                            </div>
                            <div id='digitalReview__questionChoices' className='digitalReview__questionChoices'>
                                {subject === 'Math' &&
                                    questions[questionIndex].figures.map(figure => (
                                        <ToolErrorBoundary
                                            key={figure.id}
                                            fallback={
                                                <img
                                                    className='digitalReview__figure'
                                                    src={figure.image}
                                                    alt={(questionIndex + 1).toString()}
                                                    draggable={false}
                                                />
                                            }
                                        >
                                            <FigureViewer
                                                src={figure.image}
                                                alt={(questionIndex + 1).toString()}
                                                imgClassName='digitalReview__figure'
                                            />
                                        </ToolErrorBoundary>
                                    ))
                                }
                                <p className='digitalReview__questionQuestion'>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='digitalReview__questionChoiceWrapper'>
                                                <button
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'digitalReview__questionChoice--correct'
                                                        : answers[questionIndex].answer == choice.id
                                                        ? (choice.isCorrect 
                                                            ? 'digitalReview__questionChoice--selected'
                                                            : 'digitalReview__questionChoice--wrong'
                                                        )
                                                        : 'digitalReview__questionChoice'
                                                    }
                                                    onClick={() => {}}
                                                >
                                                    <div className='digitalReview__questionChoiceOutline'></div>
                                                    <p className='digitalReview__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='digitalReview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <p className='digitalReview__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                :   <div className='digitalReview__questionAnswerReview'>
                                        <TextInput
                                            className='digitalReview__questionAnswer'
                                            inputClassName='digitalReview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={answers[questionIndex].answer
                                                ? answers[questionIndex].answer
                                                : ''
                                            }
                                            onChange={() => {}}
                                        />
                                        {showAnswer &&
                                            <p className='digitalReview__questionCorrectAnswer'>
                                                {questions[questionIndex].correctAnswers.toString().replaceAll(',', ', ')}
                                            </p>
                                        }
                                    </div>
                                }
                                <div className='digitalReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='digitalReview__dottedLine'></div>
                <div className='digitalReview__footer'>
                    {questions[questionIndex].explanationVideo
                    ?   <div className='digitalReview__buttons'>
                            <Button
                                className='digitalReview__explanationButton'
                                onClick={() => setExplanationVideo(questions[questionIndex].explanationVideo)}
                            >
                                SOLUTION VIDEO
                                <MdOutlinePlayCircle className='digitalReview__explanationButtonIcon' />
                            </Button>
                        </div>
                    :    <p className='digitalReview__tester'>
                            {studentName}
                        </p>
                    }
                    <button
                        className='digitalReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='digitalReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='digitalReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='digitalReview__buttons'>
                        <Button
                            className='digitalReview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='digitalReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='digitalReview__navigator' ref={navigatorRef}>
                            <div className='digitalReview__navigatorBubble'>
                                <h2 className='digitalReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='digitalReview__navigatorLegends'>
                                    <div className='digitalReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='digitalReview__navigatorLegendCurrent' />
                                        <p className='digitalReview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                    <div className='digitalReview__navigatorLegend'>
                                        <div className='digitalReview__navigatorLegendCorrect'></div>
                                        <p className='digitalReview__navigatorLegendText'>
                                            Correct
                                        </p>
                                    </div>
                                    <div className='digitalReview__navigatorLegend'>
                                        <div className='digitalReview__navigatorLegendWrong'></div>
                                        <p className='digitalReview__navigatorLegendText'>
                                            Incorrect
                                        </p>
                                    </div>
                                    <div className='digitalReview__navigatorLegend'>
                                        <MdOutlineBookmark className='digitalReview__navigatorLegendMarked' />
                                        <p className='digitalReview__navigatorLegendText'>
                                            For review
                                        </p>
                                    </div>
                                </div>
                                <div className='digitalReview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i].isCorrect
                                                ? 'digitalReview__navigatorItem--correct'
                                                : 'digitalReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='digitalReview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='digitalReview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='digitalReview__navigatorItemCurrent' />}
                                            {answers[i].marked && <MdOutlineBookmark className='digitalReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default DigitalReview;